import React, { useState, useEffect } from 'react';
import { X, Mic, MicOff, Wand2, Loader2 } from 'lucide-react';
import useVoiceInput from '../hooks/useVoiceInput';
import treatmentReportsService from '../services/treatmentReportsService';

/** Recording indicator ring animation */
const RecordingRing = () => (
  <span className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-75" />
);

const TextAreaPopup = ({ label, value, onChange, placeholder, isOpen, onOpen, onClose, fieldKey }) => {
  const voice = useVoiceInput(value, onChange);
  const [polishing, setPolishing] = useState(false);
  const [polishPreview, setPolishPreview] = useState(null); // { original, polished }
  const [enhanceHint, setEnhanceHint] = useState(false);

  // Stop listening when popup closes
  useEffect(() => {
    if (!isOpen && voice.isListening) {
      voice.toggle();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const runPolish = async (textToPolish) => {
    const txt = textToPolish || value;
    if (!txt || !txt.trim() || polishing) return;
    if (voice.isListening) voice.toggle();
    setPolishing(true);
    try {
      const result = await treatmentReportsService.polishText(txt, fieldKey);
      if (result.polished && result.polished !== txt) {
        setPolishPreview({ original: txt, polished: result.polished });
      }
    } catch (error) {
      console.error('Auto Polish failed:', error);
    } finally {
      setPolishing(false);
    }
  };

  // Collapsed: clicking wand opens popup AND triggers polish
  const handlePolishFromCollapsed = (e) => {
    e.stopPropagation();
    onOpen();
    runPolish();
  };

  // Collapsed: clicking mic opens popup AND starts listening
  const handleMicFromCollapsed = (e) => {
    e.stopPropagation();
    onOpen();
    // Small delay so popup mounts before starting recognition
    setTimeout(() => { if (!voice.isListening) voice.toggle(); }, 100);
  };

  const keepOriginal = () => {
    if (polishPreview) onChange(polishPreview.original);
    setPolishPreview(null);
  };

  const keepPolished = () => {
    if (polishPreview) onChange(polishPreview.polished);
    setPolishPreview(null);
  };

  const hasText = value && value.trim().length > 0;

  if (!isOpen) {
    return (
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <div className="flex items-center gap-1.5">
            {hasText && (
              <button
                type="button"
                onClick={handlePolishFromCollapsed}
                disabled={polishing}
                title="Enhance"
                className="p-1.5 rounded-md transition-all bg-gray-100 text-gray-500 hover:bg-purple-500 hover:text-white"
              >
                <Wand2 size={16} />
              </button>
            )}
            <button
              type="button"
              onClick={handleMicFromCollapsed}
              title="Voice input"
              className={`relative p-1.5 rounded-full transition-all ${
                voice.isListening
                  ? 'bg-red-500 text-white shadow-lg shadow-red-300'
                  : 'bg-gray-100 text-gray-500 hover:bg-pest-teal hover:text-white rounded-md'
              }`}
            >
              {voice.isListening && <RecordingRing />}
              {voice.isListening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className={`w-full text-left px-4 py-3 border-2 rounded-lg text-sm transition-colors ${
            value
              ? 'border-gray-300 bg-white text-gray-700 hover:border-pest-blue'
              : 'border-dashed border-gray-300 text-gray-400 hover:border-pest-blue hover:text-gray-600'
          }`}
        >
          {value
            ? <span className="line-clamp-3 whitespace-pre-wrap">{value}</span>
            : `+ Add ${label.toLowerCase()}...`
          }
        </button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 md:flex md:items-center md:justify-center md:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Mobile: full-screen white, Desktop: centred card */}
      <div className="flex flex-col bg-white h-[100dvh] md:h-auto md:max-h-[85vh] md:rounded-lg md:shadow-xl md:w-full md:max-w-3xl">
        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 md:p-4 border-b border-gray-200 md:bg-gradient-to-r md:from-pest-blue md:to-pest-teal md:rounded-t-lg flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 md:text-white">{label}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 md:text-white hover:text-gray-700 md:hover:text-gray-200 p-1 rounded-full md:hover:bg-white md:hover:bg-opacity-20">
            <X size={20} />
          </button>
        </div>

        {/* Content — grows to fill available space */}
        <div className="flex-1 min-h-0 p-3 md:p-4 overflow-y-auto">
          {polishPreview ? (
            /* Side-by-side original vs polished */
            <div className="space-y-4">
              {/* Original */}
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-600">Original</span>
                  <button
                    type="button"
                    onClick={keepOriginal}
                    className="text-xs px-3 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium transition-colors"
                  >
                    Keep
                  </button>
                </div>
                <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {polishPreview.original}
                </div>
              </div>

              {/* Enhanced */}
              <div className="border-2 border-purple-300 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between bg-white px-4 py-2.5 border-b border-purple-200">
                  <span className="text-sm font-semibold text-purple-700 flex items-center gap-1.5">
                    <Wand2 size={14} /> Enhanced
                  </span>
                  <button
                    type="button"
                    onClick={keepPolished}
                    className="text-xs px-3 py-1.5 rounded-md bg-purple-600 text-white hover:bg-purple-700 font-medium transition-colors"
                  >
                    Keep
                  </button>
                </div>
                <div className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {polishPreview.polished}
                </div>
              </div>
            </div>
          ) : (
            /* Normal textarea — fills all available space */
            <textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full h-full border-0 md:border-2 md:border-gray-300 md:rounded-lg focus:ring-0 md:focus:ring-2 md:focus:ring-pest-blue md:focus:border-pest-blue text-base md:text-sm resize-none p-0 md:px-4 md:py-3 placeholder-gray-400"
              placeholder={placeholder || `Add a note...`}
              autoFocus
            />
          )}
        </div>

        {/* Action bar — always visible above keyboard */}
        {!polishPreview && (
        <div className="flex-shrink-0 px-3 py-2 md:p-4 border-t border-gray-200 flex items-center justify-between gap-2 bg-white">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!hasText) { setEnhanceHint(true); setTimeout(() => setEnhanceHint(false), 2000); return; }
              runPolish();
            }}
            disabled={polishing}
            className="px-4 md:px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-lg hover:from-purple-700 hover:to-purple-600 font-medium transition-all disabled:opacity-50 inline-flex items-center gap-1.5 relative text-sm"
          >
            {polishing ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />}
            {polishing ? 'Enhancing...' : 'Enhance'}
            {enhanceHint && (
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-gray-800 text-white px-2 py-1 rounded">
                Add text to enhance
              </span>
            )}
          </button>
          <div className="flex items-center gap-2">
            {voice.isListening ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); voice.toggle(); }}
                className="px-4 md:px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-all inline-flex items-center gap-1.5 text-sm"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
                </span>
                Stop {voice.formatElapsed()}
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); voice.toggle(); }}
                className="px-4 md:px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition-all inline-flex items-center gap-1.5 text-sm"
              >
                <Mic size={15} />
                Record
              </button>
            )}
            <button type="button" onClick={onClose} className="px-4 md:px-6 py-2 bg-gradient-to-r from-pest-blue to-pest-teal text-white rounded-lg hover:from-blue-700 hover:to-teal-700 font-medium transition-all text-sm">
              Done
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default TextAreaPopup;
